import React, { useEffect, useState } from 'react';
import { InformationTable } from '@europrocurement/flexy-components';
import { DossierOpenSearch } from '@europrocurement/l2d-domain';
import { SxProps } from '@mui/system';
import { LegalAnnoucement } from '@europrocurement/l2d-modules/modules/Dossiers/types';
import { LegalAnnouncementCaptionInfos } from '@europrocurement/l2d-modules';
import {
    DossierArticle,
    useDossierProcesses,
    useDossierProcessesActionsAndRender,
} from '../../hooks';
import DossierArticleProcessMenu, {
    DossierArticleProcessMenuItemProps,
} from '../DossierArticleProcessMenu/DossierArticleProcessMenu';
import DossierArticleProcessStatus from '../DossierArticleProcessStatus/DossierArticleProcessStatus';
import DossierArticlesProcessIcons from '../DossierArticleProcessIcons/DossierArticleProcessIcons';

export type DossierArticlesProcessesListProps = {
    articles: DossierArticle[];
    dossier: DossierOpenSearch;
    validatorTypeId: number;
    legalAnnoucements: Array<LegalAnnoucement>;
    getItemActions?: (article: DossierArticle) => Array<DossierArticleProcessMenuItemProps>;
};

const DossierArticlesProcessesList: React.FunctionComponent<DossierArticlesProcessesListProps> =
    function ({
        articles,
        dossier,
        validatorTypeId,
        legalAnnoucements,
        getItemActions = () => [],
    }) {
        const [rows, setRows] = useState<(string | number | React.JSX.Element | null)[][]>([]);

        const { getProcessStatusColor, findProcessByArticle } = useDossierProcesses();
        const { getLegalAnnoucementByArticle } = useDossierProcessesActionsAndRender();
        const tableHeadings = ['', 'N°', 'Démarches', 'Informations', 'Statuts', ''];

        useEffect(() => {
            setRows(
                articles.map((article, index) => {
                    const process = findProcessByArticle(dossier, [validatorTypeId], article.id);

                    const legalAnnouncement = getLegalAnnoucementByArticle(
                        article.id,
                        dossier,
                        validatorTypeId,
                        legalAnnoucements,
                    );

                    return [
                        null,
                        index + 1,
                        <LegalAnnouncementCaptionInfos
                            articleLabel={article.label}
                            legalAnnouncement={legalAnnouncement}
                        />,
                        <DossierArticlesProcessIcons
                            article={article}
                            dossier={dossier}
                            validatorTypeId={validatorTypeId}
                            legalAnnoucements={legalAnnoucements}
                        />,
                        process ? (
                            <DossierArticleProcessStatus
                                statusColor={getProcessStatusColor(process)}
                                statusLabel={process?.statusLabel}
                                getItemActions={getItemActions}
                                article={article}
                            />
                        ) : (
                            ''
                        ),
                        <DossierArticleProcessMenu
                            article={article}
                            getItemActions={getItemActions}
                        />,
                    ];
                }),
            );
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [articles, dossier, legalAnnoucements, validatorTypeId, getItemActions]);

        const cellsSx: SxProps[] = [
            { width: '0%' },
            { width: '5%' },
            { width: '45%' },
            { width: '30%' },
            { width: '10%' },
            { width: '10%' },
        ];

        return (
            <InformationTable
                headers={tableHeadings}
                rows={rows}
                cellsSx={cellsSx}
            />
        );
    };

export default DossierArticlesProcessesList;
